// lib
import { assoc, difference, merge, mergeDeepRight, pluck, reject, uniq } from "ramda";
// src
import { MINUTE, ResourceType } from "config/constants";
import { userStateSuccess } from "actions/user";
import * as actions from "actions/presets";
import { hydrateSlice } from "actions/system";
import { asArray, detach, getPresetId } from "utils/presets";
import { DEFAULT_MINE_COLOR } from "utils/colors";
import { buildStorageAdapters } from "utils/storage";
import i18n from "i18n/proxy";
import { migratePrefs } from "./migrate";
import {
  addColorIfNeeded,
  addTo,
  addToById,
  removeFrom,
  removeFromById,
  removeFromWithPreset,
} from "./processors";

const maxAge = 30 * MINUTE;
const saveDelay = 1000;
const [hydrate, save] = buildStorageAdapters("presets", {
  maxAge,
  saveDelay,
  // isLight has to be reinitialized on each page load
  blacklist: ["isLight", "lastLocalIds", "version"],
});

const LIGHT_PATHNAME_PREFIX = "/plannings/light";
export const initIsLight = () => window.location.pathname.startsWith(LIGHT_PATHNAME_PREFIX);

const MINE = {
  id: "mine",
  color: DEFAULT_MINE_COLOR,
  name: {
    fr: i18n.t("menu.mine", { lng: "fr" }),
    en: i18n.t("menu.mine", { lng: "en" }),
  },
  resourceType: ResourceType.PERSON,
};

const DEFAULT_STATE = {
  isLight: initIsLight(),
  // byId: local dictionnary qualifying everything (name, params, color...)
  byId: { [MINE.id]: MINE },
  // Categories of presets
  localIds: [], // front-end only persisted
  favoriteIds: [MINE.id], // server-synced
  popularIds: [], // from server
  // Temporary
  lastLocalIds: [], // used only if DeepLink + useStateSuccess conflicts
  // The ones which needs to be fetched (checked in PresetPicker)
  enabledIds: [],
  // In light mode one preset only is enabled
  lightEnabledIds: [],
  // Preset categories
  categories: [],
  // to patch
  version: undefined,
};

const pluckId = (presets) => pluck("id", asArray(presets));

const updateByIdColors = (byId, ids) => {
  const diffById = addColorIfNeeded(byId, ids);
  return diffById ? addToById(byId, diffById) : byId;
};

export default (state = DEFAULT_STATE, { type, payload }) => {
  // order actions by frequency
  switch (type) {
    case actions.addToEnableds.toString(): {
      // OPTIMIZE: byId need not always being updated
      return save({
        ...state,
        byId: updateByIdColors(state.byId, payload),
        enabledIds: addTo(state.enabledIds, payload),
      });
    }
    case actions.removeFromEnableds.toString(): {
      return save({
        ...state,
        enabledIds: removeFrom(state.enabledIds, payload),
      });
    }
    case actions.addToLocalsAndEnable.toString(): {
      // we want to add to localIds only if not in favoriteIds, but we want to enable anyway
      // first we filter if already in favorites
      const notFavorites = removeFromWithPreset(payload, state.favoriteIds);
      const notFavoriteIds = pluckId(notFavorites);
      return save({
        ...state,
        byId: addToById(updateByIdColors(state.byId, notFavoriteIds), notFavorites),
        enabledIds: addTo(state.enabledIds, pluckId(payload)),
        localIds: addTo(state.localIds, notFavoriteIds),
        lastLocalIds: notFavoriteIds,
      });
    }
    case actions.addToFavoritesAndEnable.toString(): {
      const ids = pluckId(payload);
      // special case: already in localIds
      return save({
        ...state,
        byId: addToById(updateByIdColors(state.byId, ids), payload),
        enabledIds: addTo(state.enabledIds, ids),
        favoriteIds: addTo(state.favoriteIds, ids),
        localIds: removeFrom(state.localIds, ids),
      });
    }
    case actions.exclusiveLightEnable.toString(): {
      return save({
        ...state,
        byId: addToById(state.byId, payload),
        lightEnabledIds: [payload.id],
      });
    }
    case actions.qualifyPreset.toString(): {
      return save({
        ...state,
        byId: addToById(state.byId, payload),
      });
    }
    case actions.removeFromFavorites.toString(): {
      const id = payload;
      const detached = detach(state.byId[id]);
      return save({
        ...state,
        favoriteIds: removeFrom(state.favoriteIds, id),
        byId: assoc(id, detached, state.byId),
      });
    }
    case actions.removeFromLocals.toString(): {
      return save({
        ...state,
        localIds: removeFrom(state.localIds, payload),
      });
    }
    case actions.popularsSuccess.toString(): {
      const { categories, presets: populars } = payload;
      return save({
        ...state,
        categories,
        byId: addToById(state.byId, populars),
        popularIds: pluckId(populars),
      });
    }
    case actions.destroyPreset.toString(): {
      // In case of a 404 for instance
      const id = getPresetId(payload);
      return save({
        ...state,
        enabledIds: removeFrom(state.enabledIds, id),
        byId: removeFromById(state.byId, id),
      });
    }
    case userStateSuccess.toString(): {
      const prefs = migratePrefs(payload.prefs);
      // destructure with default values
      const {
        byId: serverById = {},
        enabledIds: serverEnabledIds = [],
        favoriteIds: serverFavoriteIds = state.favoriteIds,
        version,
      } = prefs;
      const { lastLocalIds } = state;

      // case: in localStorage as favorite and popular, if removed from server should be disabled even as popular
      const notInServerFavoriteIds = removeFrom(state.favoriteIds, serverFavoriteIds);
      // case (merging previous one): enabled in localStorage, disabled in server => should be disabled
      let disabledIds = uniq(serverFavoriteIds, notInServerFavoriteIds);
      // if has just been added from DeepLink, userStateSuccess should not disable it
      disabledIds = reject((id) => lastLocalIds.includes(id), disabledIds);
      const newEnabledIds = addTo(removeFrom(state.enabledIds, disabledIds), serverEnabledIds);

      return save({
        ...state,
        byId: mergeDeepRight(state.byId, serverById),
        enabledIds: newEnabledIds,
        favoriteIds: serverFavoriteIds,
        version,
      });
    }
    case actions.updateFavorites.toString(): {
      const { byId, favoriteIds, removedCategoryIds } = payload;
      const removedIds = difference(state.favoriteIds, favoriteIds);
      // payload byId is not deep merge into state: it replaces it
      // since some fields (underId for instance) may be removed
      return save({
        ...state,
        byId: merge(removeFromById(state.byId, removedCategoryIds), byId),
        enabledIds: removeFrom(state.enabledIds, removedIds),
        favoriteIds,
      });
    }
    case hydrateSlice.toString(): {
      const { isLight } = state;
      const hydratedState = hydrate(state, payload);
      const { lightEnabledIds } = hydratedState;
      if (isLight || lightEnabledIds.length === 0) return hydratedState;
      // add lightEnabledIds to enabledIds as locals
      const newLocalIds = addTo(state.localIds, lightEnabledIds);
      const newEnabledIds = addTo(state.enabledIds, lightEnabledIds);
      return save({
        ...hydratedState,
        byId: updateByIdColors(hydratedState.byId, newEnabledIds),
        enabledIds: newEnabledIds,
        lightEnabledIds: [], // flush
        localIds: newLocalIds,
      });
    }
    default:
      return state;
  }
};
